import {cloneDeep, debounce} from 'lodash';
import {Store} from 'redux';

import {logger} from '../../srcCommon/services/logger/logger';
import {AppState} from '../store/appState';
import {getCompany} from '../store/data/company/companySelectors';
import {postAsync} from './http/http';

// Datadog logger used to log if the Logstash buffer exceeds its limit.
const logstashLogger = logger.getLogger('logstash');

// Drop logs if there are too many waiting to be sent.
const maxBufferCount = 100000;

const baseDelay = 30 * 1000;
const maxDelay = 3 * 60 * 1000;

// Random component to avoid DDoS'ing the server if the clients are force reloaded at the same time.
const jitterDelay = 30 * 1000;

let pendingLogs: LogstashEntry[] = [];

let logstashEndpoint: string | undefined;

const jitteredBaseDelay = baseDelay + Math.random() * jitterDelay;
const jitteredMaxDelay = maxDelay + Math.random() * jitterDelay;

const submitLogstashDebounced = debounce(submitLogstash, jitteredBaseDelay, {
  leading: false,
  trailing: true,
  maxWait: jitteredMaxDelay,
});

let hasWarnedForDroppedLogs = false;

interface LogstashEntry {
  action: string;
}

/* eslint-disable camelcase */
interface CounterPushReceive extends LogstashEntry {
  action: 'counters-push-received';
  counter: {
    name: string;
    ids: {
      companyId: number;
      teammateId?: number;
      inboxId?: number;
      tagId?: number;
      teamId?: number;
      otherTeammateId?: number;
    };
  };
  uid?: string;
  value?: number;
  received_at: string;
  teammate_id: number;
  session_id: string;
}
/* eslint-enable camelcase */

// Update the backend endpoint if you want to update these types or add new types.
type LogstashLog = CounterPushReceive;

export function setupLogstash(store: Store<AppState>) {
  const company = getCompany(store.getState());
  logstashEndpoint = `${company.url}/log`;
}

async function submitLogstash() {
  if (!logstashEndpoint) {
    return;
  }

  const logsToSend = pendingLogs;
  pendingLogs = [];

  const body = {
    events: logsToSend,
  };

  try {
    await postAsync(logstashEndpoint, body);

    // The buffer was successfully flushed, so we can start warning again if we drop logs.
    hasWarnedForDroppedLogs = false;
  } catch (error) {
    // In case of failure, merge back the logs at the beginning of the buffer and try again later.
    pendingLogs = logsToSend.concat(pendingLogs);
    submitLogstashDebounced();
  }
}

/**
 * Send a structured log entry to Logstash. This is helpful in cases where you need server logs and frontend logs to be in the same place in order to analyze them more easily on Kibana.
 * This service will drop logs if the client is closed before it gets the chance periodically flush its buffer.
 * Prefer using the logger service which sends logs to Datadog logs and is more reliable.
 */
export function logToLogstash(entry: LogstashLog): void {
  // Log to Datadog (only once) if the pending logs buffer is already full.
  if (pendingLogs.length >= maxBufferCount && !hasWarnedForDroppedLogs) {
    logstashLogger.error('Dropping log entry due to buffer exceeding its maximum size');
    hasWarnedForDroppedLogs = true;
  } else {
    pendingLogs.push(cloneDeep(entry));
  }

  submitLogstashDebounced();
}
